<script lang="ts" setup>
import type { SubjectCode, SubjectViewProps } from '~/models/Subject'
import type { ProductHeaders } from '~/models/ProductHeaders'
import type { ContentProductContainer } from '~/models/Content/ContentProductContainer'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { grades8to10 } from '~/utils/gradeSorter'
import { forceColorShade } from '~/utils/colors'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import TeacherOverrideButton from '~/components/subject/TeacherOverrideButton.vue'
import TeacherButton from '~/components/subject/TeacherButton.vue'
import SubjectToolbox from '~/components/subject/SubjectToolbox.vue'
import SubjectSearch from '~/components/subject/SubjectSearch.vue'
import ColophonButton from '~/components/subject/ColophonButton.vue'
import UpcomingSubjectButton from '~/components/ResourceFinder/UpcomingSubjectButton.vue'
import LegacySubjectButton from '~/components/ResourceFinder/LegacySubjectButton.vue'
import GradeFilter from '~/components/ResourceFinder/GradeFilter.vue'
import SubjectCard from '~/components/cards/SubjectCard.vue'

const props = defineProps<SubjectViewProps>()

const { t } = useI18n()
const router = useRouter()
const { setCurrentSubject } = useSubjectsStore()
const { selectedGrade } = storeToRefs(useFilterStore())
const { isTeacher } = storeToRefs(useAuthStore())
const { findProductsByGradeAndSubject, getRelatedLocationsByGradeAndSubject } = useProductStore()
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { getProductHeadersBySubjectAndGrade, findContents } = useContentApi()
const { set: setAppColor, clear: clearAppColor } = useAppColor()

const containers = ref<{
  upcomingLocationIds: number[]
  archivedLocationIds: number[]
  learningPathsLocationIds: number[]
}>()

const showTeacherButton = computed(() => isTeacher.value
  && hasLoadedProducts.value
  && selectedGrade.value
  && props.subjectCode
)

const hideGradeFilter = computed(() => ['NOR', 'KRL'].includes(props.subjectCode)
  && selectedGrade.value
  && grades8to10.includes(selectedGrade.value))

const headers = computed(() => {
  if (!data?.value)
    return []
  const key: keyof ProductHeaders = props.isUpcoming ? 'upcomingContent' : 'learningPath'
  return data.value[key]
})

const hasArchive = computed(() => !!data?.value?.archivedContent?.length)
const hasUpcoming = computed(() => !!data?.value?.upcomingContent?.length)

const archiveUri = computed(() => {
  if (props.isUpcoming) {
    return router.resolve({ name: 'subject', params: { subject: props.subjectCode.toLowerCase() } }).href
  }
  if (hasArchive.value) {
    return router.resolve({ name: 'subject_archive', params: { subject: props.subjectCode.toLowerCase() } }).href
  }
  return null
})

const intro = computed(() => selectedGrade.value
  ? findProductsByGradeAndSubject(selectedGrade.value, props.subjectCode)
    .find(({ aunivers }) => !!aunivers?.intro)
    ?.aunivers
    ?.intro ?? ''
  : '')

const { data, isLoading } = useQuery({
  enabled: computed(() => !!selectedGrade.value),
  queryKey: computed(() => ['subject-headers', props.subjectCode, selectedGrade.value]),
  queryFn: () => getProductHeadersBySubjectAndGrade(props.subjectCode, selectedGrade.value!),
  staleTime: Infinity,
})

const teacherOverrideRelation = computed(() => {
  if (!containers.value) {
    return []
  }
  if (props.isUpcoming) {
    return containers.value.upcomingLocationIds
  }
  if (props.isArchive) {
    return containers.value.archivedLocationIds
  }
  if (!props.isUpcoming && !props.isArchive) {
    return containers.value.learningPathsLocationIds
  }
  return []
})

const { data: teacherOverrideContainer } = useQuery({
  staleTime: Infinity,
  enabled: computed(() => teacherOverrideRelation.value.length > 0),
  queryKey: computed(() => ['teacher-override', teacherOverrideRelation.value]),
  queryFn: () => findContents<ContentProductContainer>({
    contentTypeCriterion: [ContentType.ProductContainer],
    locationIdCriterion: teacherOverrideRelation.value,
  }, 1).then(([data]) => data)
})

const teacherOverrideGuide = computed(() => {
  return teacherOverrideContainer?.value?.teacherRelation?.destinationContentIds[0]
})

const themeColor = computed(() => {
  if (!props.subjectCode)
    return ColorName.Blue5
  return ({
    SAF: ColorName.Violet5,
    ENG: ColorName.Coral5,
    NOR: ColorName.Yellow5,
    KRL: ColorName.Purple5,
    SPA: ColorName.Coral5,
    MAT: ColorName.Orange5,
    NAT: ColorName.Blue5,
    MUS: ColorName.Green5,
    KRO: ColorName.Coral5,
    TYS: ColorName.Seagreen5,
    FRA: ColorName.Green5,
  } as Partial<Record<SubjectCode, string>>)[props.subjectCode] ?? ColorName.Blue5
})

const themeColorValue = computed(() => forceColorShade(themeColor.value, '10'))
const skeletonColor = computed(() => forceColorShade(themeColor.value, '30'))

onBeforeUnmount(() => setCurrentSubject(undefined))
onBeforeUnmount(() => clearAppColor())

onMounted(() => {
  if (themeColorValue.value) {
    setAppColor(themeColorValue.value)
  }
})

watch(themeColorValue, (color) => {
  setAppColor(color)
}, { immediate: true })

watch(props, () => {
  setCurrentSubject(props.subjectCode)
}, { immediate: true })

watch(selectedGrade, () => {
  router.replace({
    name: props.isUpcoming ? 'subject_upcoming' : 'subject',
    params: {
      subject: props.subjectCode.toLowerCase(),
    },
    query: {
      grade: selectedGrade.value
    }
  })
})

watch(selectedGrade, async () => {
  containers.value = await getRelatedLocationsByGradeAndSubject(selectedGrade.value!, props.subjectCode)
}, { immediate: true })
</script>

<template>
  <div>
    <div class="color-black relative mx-auto size-full max-w-[1084px] px-4 sm:px-8">
      <div class="mx-auto flex-1 space-y-6 pt-32 transition-all">
        <div class="flex justify-between gap-6 max-md:flex-wrap">
          <section class="flex w-full flex-wrap items-center gap-6">
            <h1 class="text-3xl font-bold md:text-5xl" v-text="t(`metadata.subjects.${subjectCode}`)" />
            <Teleport
              v-if="!hideGradeFilter"
              to="#header-navigation"
              defer
            >
              <GradeFilter :color="themeColorValue" />
            </Teleport>
          </section>
        </div>
        <p
          v-if="intro"
          class="w-2/3 whitespace-pre-line"
          v-text="intro"
        />
        <section class="flex items-center gap-5">
          <TeacherOverrideButton
            v-if="showTeacherButton && selectedGrade && teacherOverrideGuide"
            :content-id="teacherOverrideGuide"
            :grade-code="selectedGrade"
            :subject-code="subjectCode"
          />
          <TeacherButton
            v-else-if="showTeacherButton && selectedGrade"
            :data-pendo="PendoFeature.TeacherContent.Subject"
            :grade-code="selectedGrade"
            :subject-code="subjectCode"
            color="teacher"
          />
          <LegacySubjectButton
            v-if="archiveUri"
            :color="themeColor"
            :uri="archiveUri"
          />
          <UpcomingSubjectButton
            v-if="hasUpcoming && !isUpcoming"
            :color="themeColor"
            :subject-code="subjectCode"
          />
        </section>
        <section class="space-y-4">
          <AuSkeletonWrapper
            v-if="isLoading"
            class="mt-12 grid w-full grid-cols-1 gap-20 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          >
            <AuSkeleton
              v-for="i in 8"
              :key="`skeleton-${i}`"
              :background="skeletonColor"
              height="230px"
            />
          </AuSkeletonWrapper>
          <ul
            v-else-if="selectedGrade"
            class="max-w-screen-au mt-12 grid w-full grid-cols-2 gap-6 md:grid-cols-3 md:gap-12 lg:grid-cols-4"
          >
            <li
              v-for="resource in headers"
              :key="`card-${resource.locationId}`"
            >
              <SubjectCard
                :is-upcoming
                :resource
                :selected-grade
                :subject-code
              />
            </li>
          </ul>
        </section>
      </div>
    </div>
    <SubjectToolbox
      v-if="hasLoadedProducts && selectedGrade"
      :grade="selectedGrade"
      :subject="subjectCode"
    />
    <div class="mx-auto mb-12 max-w-[1084px] px-4 sm:px-8">
      <SubjectSearch
        v-if="hasLoadedProducts && selectedGrade"
        :grade-code="selectedGrade"
        :subject-code="subjectCode"
        :theme-color="themeColor"
      />
      <ColophonButton
        v-if="hasLoadedProducts && selectedGrade"
        :color="themeColor"
        :data-pendo="PendoFeature.Colophon.Subject"
        :grade-code="selectedGrade"
        :subject-code="subjectCode"
      />
    </div>
  </div>
</template>
